// MSAL-based auth strategy for Azure AD / Azure Digital Twins.
//
// Spec: docs/auth-strategy.md
// Plan: docs/REC-API-MIGRATION.md (step 3)
//
// Wraps a `PublicClientApplication` so the plugin layer (vue-wob-cache) holds
// a strategy reference rather than an MSAL instance directly. A future
// `AwsSigV4Auth` for the WoB REC API (PCX) will implement the same surface so
// it can be swapped in without further changes to the plugin or client.

import {
  PublicClientApplication,
  InteractionRequiredAuthError,
} from '@azure/msal-browser'

const DEFAULT_SCOPES = ['https://digitaltwins.azure.net/.default']

class MsalAuth {
  constructor({ clientId, tenantId, redirectUri, scopes } = {}) {
    this.clientId = clientId
    this.tenantId = tenantId
    this.redirectUri = redirectUri
    this.scopes = scopes && scopes.length ? scopes : DEFAULT_SCOPES

    this._msal = new PublicClientApplication({
      auth: {
        clientId,
        authority: `https://login.microsoftonline.com/${tenantId}`,
        redirectUri,
      },
      scopes: this.scopes,
      cache: {
        cacheLocation: 'sessionStorage',
        storeAuthStateInCookie: false,
      },
    })

    this._initialized = false
  }

  // One-time MSAL initialization. Idempotent; safe to await again.
  async initialize() {
    if (this._initialized) return
    await this._msal.initialize()
    this._initialized = true
  }

  // Interactive sign-in. Resolves to { token, account } on success.
  async login() {
    const response = await this._msal.loginPopup()
    const tokenResponse = await this._msal.acquireTokenPopup({
      scopes: this.scopes,
      account: response.account,
    })
    return { token: tokenResponse.accessToken, account: response.account }
  }

  // Interactive sign-out. Looks up the account by homeAccountId so the popup
  // targets the right session, and routes the post-logout redirect to the
  // configured redirect URI.
  async logout({ homeAccountId } = {}) {
    const account =
      homeAccountId != null
        ? this._msal.getAccountByHomeId(homeAccountId)
        : null
    await this._msal.logoutPopup({
      account,
      mainWindowRedirectUri: this.redirectUri,
    })
  }

  // Silent token acquisition with popup fallback on InteractionRequiredAuthError.
  // Assigned to `client.tokenProvider` so the client can refresh tokens before
  // each request without round-tripping through the plugin layer.
  // Returns null when no account is signed in or both paths fail.
  async getToken() {
    const accounts = this._msal.getAllAccounts()
    if (accounts.length === 0) return null
    const request = { scopes: this.scopes, account: accounts[0] }
    try {
      const response = await this._msal.acquireTokenSilent(request)
      return response.accessToken
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        console.log(
          'MsalAuth: silent token refresh failed, falling back to popup'
        )
        try {
          const response = await this._msal.acquireTokenPopup(request)
          return response.accessToken
        } catch (popupError) {
          console.error(
            'MsalAuth: token refresh via popup also failed',
            popupError
          )
          return null
        }
      }
      console.error('MsalAuth: token refresh error', error)
      return null
    }
  }

  // Returns the first signed-in account, or null. The current explorer assumes
  // a single account; multi-account UX can extend this later.
  getAccount() {
    const [account] = this._msal.getAllAccounts()
    return account || null
  }

  // Look up an account by MSAL homeAccountId (`localAccountId.tenantId`).
  // Used at logout time to scope the popup to the correct session.
  getAccountById(homeAccountId) {
    if (homeAccountId == null) return null
    return this._msal.getAccountByHomeId(homeAccountId) || null
  }
}

export { MsalAuth }
